// topoEditor/topoDoc.js — the "Nabe": a canonical token model with pluggable input and output paths.
//
// Tokens (segments) are the hub. Everything converts TO tokens or FROM tokens, so each editor (Preview,
// Live) only ever talks to the hub — never to another editor's format. Pure / UI-less (no mounting); the
// HTML edge parses via a detached DOM node, which also works under jsdom in tests.
//
//        INPUTS  → tokens                         tokens →  OUTPUTS
//   parseTokensFromConvention   ─┐        ┌─  renderEditorHtmlFromTokens   (editor display)
//   parseTokensFromEditorHtml   ─┼─ tokens ─┼─  renderFrontendHtmlFromTokens (public <a target=_blank>)
//   parseTokensFromJson         ─┘        ├─  renderConventionFromTokens   (the stored plain-text string)
//   parseTokensFromAny (detects)          └─  tokensToJson                 (structured DB shape — later)
//
// Token shape (see tokenize.js): { type:'text', text } | { type:'entity', kind, data, raw? }.
// Entity edges used here (see entities/*): parse/pattern (convention→), serialize (→convention),
// renderHtml (→editor html), renderFrontendHtml (→frontend html), matchElement (editor html→).
//
// STORAGE STAYS the convention string for now (decision 2026-07-09) — JSON tokens are an experiment surface
// (playground + headless), not the persisted form yet.

import { tokenize } from "./tokenize.js";
import { renderSegments, escapeHtml } from "./render.js";

// ── inputs → tokens ─────────────────────────────────────────────────────────
export function parseTokensFromConvention(text, entities) {
    return tokenize(text ?? "", entities);
}

// Parse OUR editor HTML (a string or a DOM node) into tokens. Recognizes only elements an entity claims via
// matchElement; <br> and block wrappers (browsers insert <div>/<p> on Enter) become newlines; unknown
// elements are unwrapped (their text kept). Adjacent text is merged so the result matches tokenize() shape.
export function parseTokensFromEditorHtml(html, entities) {
    const root = typeof html === "string" ? htmlToNode(html) : html;
    const raw = [];
    walkHtml(root, entities, raw);
    return mergeAdjacentText(raw);
}

export function parseTokensFromJson(json) {
    const arr = typeof json === "string" ? JSON.parse(json) : json;
    if (!isTokensArray(arr)) { throw new Error("topoDoc: not a tokens array"); }
    return normalizeTokens(arr);
}

// Detect the representation of a stored field value: a JSON tokens array, or the convention string.
// A convention like "[x](y)" is NOT valid JSON → JSON.parse throws → we fall back to convention parsing.
export function parseTokensFromAny(value, entities) {
    if (Array.isArray(value)) { return normalizeTokens(value); }
    const s = String(value ?? "");
    try {
        const a = JSON.parse(s);
        if (isTokensArray(a)) { return normalizeTokens(a); }
    } catch (e) { /* not JSON → treat as convention */ }
    return parseTokensFromConvention(s, entities);
}

// ── tokens → outputs ────────────────────────────────────────────────────────
export function renderConventionFromTokens(tokens, entities) {
    const byKind = new Map(entities.map((e) => [e.kind, e]));
    return tokens.map((s) => {
        if (s.type === "text") { return s.text; }
        const e = byKind.get(s.kind);
        if (e && typeof e.serialize === "function") { return e.serialize(s.data); }
        return s.raw ?? "";   // tokens built from a convention string keep their exact raw match as fallback
    }).join("");
}

export function renderEditorHtmlFromTokens(tokens, entities, placeholder) {
    return renderSegments(tokens, entities, placeholder);
}

// Tokens -> contenteditable (TopoLiveEditor) HTML. Like the editor-display render, but each entity may emit
// a distinct LIVE form via renderLiveHtml (a matchElement-able, in-place-editable element) — falls back to
// renderHtml, then to escaped raw. No placeholder span here (the live editor shows its own via .is-empty).
export function renderLiveHtmlFromTokens(tokens, entities) {
    const byKind = new Map(entities.map((e) => [e.kind, e]));
    return tokens.map((s) => {
        if (s.type === "text") { return escapeHtml(s.text).replace(/\n/g, "<br>"); }
        const e = byKind.get(s.kind);
        if (e && typeof e.renderLiveHtml === "function") { return e.renderLiveHtml(s.data); }
        if (e && typeof e.renderHtml === "function") { return e.renderHtml(s.data, 0); }
        return escapeHtml(s.raw ?? "");
    }).join("");
}

export function renderFrontendHtmlFromTokens(tokens, entities) {
    const byKind = new Map(entities.map((e) => [e.kind, e]));
    return tokens.map((s) => {
        if (s.type === "text") { return escapeHtml(s.text).replace(/\n/g, "<br>"); }
        const e = byKind.get(s.kind);
        if (e && typeof e.renderFrontendHtml === "function") { return e.renderFrontendHtml(s.data); }
        return escapeHtml(e && e.serialize ? e.serialize(s.data) : (s.raw ?? ""));   // no frontend renderer → inert text
    }).join("");
}

// Structured shape for (future) DB storage: drop derived `raw`, keep type/kind/data/text only.
export function tokensToJson(tokens) {
    return JSON.stringify(tokens.map((s) =>
        s.type === "text" ? { type: "text", text: s.text } : { type: "entity", kind: s.kind, data: s.data }
    ));
}

// ── headless convenience: field value (any input) → frontend HTML, no editor UI ──
export function renderFrontendHtmlFromValue(value, entities) {
    return renderFrontendHtmlFromTokens(parseTokensFromAny(value, entities), entities);
}

// ── internals ────────────────────────────────────────────────────────────────
function htmlToNode(html) {
    const d = document.createElement("div");
    d.innerHTML = html;
    return d;
}

function matchEntity(el, entities) {
    for (const e of entities) {
        if (typeof e.matchElement === "function") {
            const data = e.matchElement(el);
            if (data) { return { kind: e.kind, data }; }
        }
    }
    return null;
}

function walkHtml(node, entities, out) {
    node.childNodes.forEach((child) => {
        if (child.nodeType === 3) { out.push({ type: "text", text: child.nodeValue }); return; }   // text node
        if (child.nodeType !== 1) { return; }                                                       // comment etc.
        if (child.nodeName === "BR") { out.push({ type: "text", text: "\n" }); return; }
        const hit = matchEntity(child, entities);
        if (hit) { out.push({ type: "entity", kind: hit.kind, data: hit.data }); return; }
        if (child.nodeName === "DIV" || child.nodeName === "P") {
            const last = out[out.length - 1];
            if (out.length && !(last.type === "text" && last.text.endsWith("\n"))) { out.push({ type: "text", text: "\n" }); }
            walkHtml(child, entities, out);
            return;
        }
        walkHtml(child, entities, out);   // unknown element → unwrap, keep children
    });
}

function mergeAdjacentText(segs) {
    const out = [];
    segs.forEach((s) => {
        const last = out[out.length - 1];
        if (s.type === "text" && last && last.type === "text") { last.text += s.text; }
        else if (s.type === "text") { out.push({ type: "text", text: s.text }); }
        else { out.push(s); }
    });
    return out.filter((s) => s.type !== "text" || s.text !== "");
}

function isTokensArray(a) {
    return Array.isArray(a) && a.every((s) => s && (s.type === "text" || s.type === "entity"));
}

function normalizeTokens(arr) {
    return arr.map((s) => s.type === "text"
        ? { type: "text", text: s.text ?? "" }
        : { type: "entity", kind: s.kind, data: s.data ?? {} });
}
